import { ChangeEvent, forwardRef } from "react";
import styled from "@emotion/styled";
import { Controller, FieldError, RegisterOptions, useFormContext } from "react-hook-form";
import { InputBaseComponentProps } from "@mui/material/InputBase";
import { StyledInput, FormFieldError } from "../form/FormElementStyles";
import { Container, StyledFormLabel } from "./CommonStyles";
import { Typography } from "@kaltura/ds-react-components";

export interface TextFieldProps {
    label: string;
    id: string;
    description?: string;
    placeholder?: string;
    defaultValue?: string;
    error?: FieldError;
    errorMessage?: string;
    required?: boolean;
    type?: string;
    onChange?: (event: ChangeEvent<HTMLTextAreaElement>) => void;
    additionalInfo?: string;
    fontWeightBold?: boolean;
    registerOptions?: RegisterOptions;
    inline?: boolean;
    readonly?: boolean;
    disabled?: boolean;

    /**
     * root class name
     */
    className?: string;

    /**
     * text to show after the input control
     */
    postText?: string;

    displayErrors?: boolean;

    inputProps?: InputBaseComponentProps;

    /**
     * ID of the element that describes this input for accessibility
     */
    ariaDescribedBy?: string;
}

const Row = styled("div")(({ theme }) => ({
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(1),
    alignItems: "center",
}));

/**
 * text input field for edit entry form
 */
export const TextField = forwardRef<any, TextFieldProps>((props: TextFieldProps, ref) => {
    const {
        label,
        id,
        placeholder,
        required,
        type = "text",
        description,
        defaultValue,
        additionalInfo,
        fontWeightBold,
        registerOptions,
        inline = false,
        readonly = false,
        disabled = false,
        className,
        postText,
        displayErrors = false,
        inputProps,
        ariaDescribedBy,
    } = props;

    const { control } = useFormContext();

    return (
        <Container inline={inline} className={className}>
            <StyledFormLabel
                htmlFor={id}
                required={required}
                text={label}
                description={description}
                additionalInfo={additionalInfo}
                fontWeightBold={fontWeightBold}
            />
            <Controller
                control={control}
                name={id}
                defaultValue={defaultValue ?? ""}
                rules={{
                    ...(registerOptions || {}),
                    required: disabled ? false : registerOptions?.required ?? required,
                }}
                render={({ field: { onChange, onBlur, value, ref }, fieldState: { error } }) => {
                    // Generate error element ID and build complete aria-describedby
                    const errorId = `${id}-error`;
                    const describedByIds = [ariaDescribedBy, displayErrors && error ? errorId : null]
                        .filter(Boolean)
                        .join(" ");

                    return (
                        <>
                            <Row>
                                <StyledInput
                                    id={id}
                                    inputRef={ref}
                                    value={value}
                                    error={!!error}
                                    type={type}
                                    aria-invalid={error ? "true" : "false"}
                                    aria-describedby={describedByIds || undefined}
                                    placeholder={placeholder}
                                    required={required}
                                    onChange={onChange}
                                    onBlur={onBlur}
                                    readOnly={readonly}
                                    disabled={disabled}
                                    inputProps={inputProps}
                                />
                                {postText && (
                                    <Typography variant={"body2"} className={"kms-ds-textfield-posttext"}>
                                        {postText}
                                    </Typography>
                                )}
                            </Row>
                            {displayErrors && error?.message && (
                                <FormFieldError id={errorId}>{error.message as string}</FormFieldError>
                            )}
                        </>
                    );
                }}
            />
        </Container>
    );
});
TextField.displayName = "TextField";
